// Everything about read state for the comms page: the per-channel server
// sync, the "mark read on open" side effect, and the frozen snapshots the
// "New messages" line and the "unread above" pill are drawn from.
//
// The unread store (`useUnread`) is handed in rather than read from context,
// so the page mounts the two providers in either order and a test can hand
// in a plain object.

import { useEffect, useRef, useState } from 'react';
import { useClientApis } from '../../hooks/ClientApisProvider.js';
import type { ChannelSummary } from '../../interfaces/ICommsApi.js';
import type { IUnreadValue } from './useUnread.js';

export interface IUseChannelReadStateOptions {
  siteId: string;
  channels: ChannelSummary[];
  channelsFetching: boolean;
  activeChannelId: string | null;
  activeLastMessageAt: string | undefined;
  /**
   * The open conversation is not in front (a screen pushed over it, another
   * tab): keep its snapshots, but do not mark what lands meanwhile as read.
   */
  paused?: boolean;
  /** The app's unread store — `useUnread()` — passed in so the hook can be rendered with any store. */
  store: IUnreadValue;
}

export function useChannelReadState({
  siteId,
  channels,
  channelsFetching,
  activeChannelId,
  activeLastMessageAt,
  paused = false,
  store,
}: IUseChannelReadStateOptions) {
  const { markChannelRead } = useClientApis().comms;
  const { lastReadMap, countMap, markRead, seedFromServer } = store;

  // The "New messages" divider sits above the first message newer than this
  // cursor. Snapshotted when you switch INTO a channel, then frozen — so the
  // divider stays put as you read (markRead advancing the real cursor must not
  // move it). It is recomputed on the next switch-in: by then everything you
  // saw is read, so the line drops to whatever arrived while you were away, or
  // disappears if nothing did.
  const [unreadSinceAt, setUnreadSinceAt] = useState<string | undefined>(undefined);
  
  // How many were unread at that switch-in — the "unread above" pill's count,
  // frozen the same way (markRead zeroes the live count at once).
  const [unreadAtOpenCount, setUnreadAtOpenCount] = useState(0);
  const [trackedChannelId, setTrackedChannelId] = useState<string | null>(null);

  // Re-snapshot the divider cursor during render whenever the active channel
  // changes — reading lastReadMap as it stands now, before the effect below
  // advances it via markRead. The guard makes it run only on a real switch.
  if (activeChannelId !== trackedChannelId) {
    setTrackedChannelId(activeChannelId);
    setUnreadSinceAt(activeChannelId ? lastReadMap[activeChannelId] : undefined);
    setUnreadAtOpenCount(activeChannelId ? (countMap[activeChannelId] ?? 0) : 0);
  }

  // Seed counts from the server only when a real fetch completes (initial load,
  // window-focus refetch) — not on the WS cache patches, which keep `isFetching`
  // false and must not clobber live increments.
  const wasFetchingRef = useRef(false);
  useEffect(() => {
    const justFetched = wasFetchingRef.current && !channelsFetching;
    wasFetchingRef.current = channelsFetching;
    if (!justFetched) return;
    seedFromServer(
      channels.map((ch) => ({ channelId: ch.id, unreadCount: ch.unreadCount ?? 0, lastReadAt: ch.lastReadAt })),
      activeChannelId,
    );
  }, [channelsFetching, channels, activeChannelId, seedFromServer]);

  useEffect(() => {
    if (!activeChannelId || paused) return;
    // Advance the read cursor to the latest message and tell the server. Runs
    // on switch-in AND when a new message lands while the channel is open; the
    // divider snapshot above is intentionally left untouched here so it stays
    // frozen while you read.
    markRead(activeChannelId, activeLastMessageAt ?? new Date().toISOString());
    void markChannelRead(siteId, activeChannelId).catch(() => {});
  }, [activeChannelId, activeLastMessageAt, paused, markRead, markChannelRead, siteId]);

  return { unreadSinceAt, unreadAtOpenCount, lastReadMap, countMap };
}
